import { useState } from 'react'
import { supabase } from './supabase'

export default function Auth() {
  const [mode, setMode] = useState('login')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [message, setMessage] = useState(null)

  async function handleSignUp() {
    setLoading(true)
    setError(null)
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim()
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name: fullName } },
    })
    if (error) { setError(error.message); setLoading(false); return }
    if (data.user) {
      await supabase.from('profiles').upsert({ id: data.user.id, name: fullName })
    }
    setMessage('Account created! Check your email to confirm.')
    setLoading(false)
  }

  async function handleLogin() {
    setLoading(true)
    setError(null)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) { setError(error.message); setLoading(false); return }
    setLoading(false)
  }

  async function handleForgotPassword() {
    if (!email) { setError('Enter your email address first.'); return }
    setLoading(true)
    setError(null)
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    })
    if (error) { setError(error.message); setLoading(false); return }
    setMessage('Check your email for a password reset link.')
    setLoading(false)
  }

  function switchMode(next) {
    setMode(next)
    setError(null)
    setMessage(null)
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.brand}>
          bun<span style={styles.brandGreen}>dl</span>e
        </div>
        <div style={styles.tagline}>
          {mode === 'forgot'
            ? "Enter your email and we'll send you a reset link."
            : 'Your weekly plan and shop, done in minutes.'}
        </div>

        {mode === 'signup' && (
          <div style={styles.nameRow}>
            <input
              style={styles.input}
              placeholder="First name"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
            />
            <input
              style={styles.input}
              placeholder="Last name"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
            />
          </div>
        )}

        <input
          style={styles.input}
          placeholder="Email address"
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
        />

        {mode !== 'forgot' && (
          <input
            style={styles.input}
            placeholder="Password"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
        )}

        {mode === 'login' && (
          <button style={styles.forgotLink} onClick={() => switchMode('forgot')}>
            Forgot password?
          </button>
        )}

        {error && <div style={styles.error}>{error}</div>}
        {message && <div style={styles.messageBox}>{message}</div>}

        <button
          style={styles.btnPrimary}
          onClick={mode === 'signup' ? handleSignUp : mode === 'forgot' ? handleForgotPassword : handleLogin}
          disabled={loading}
        >
          {loading
            ? 'Please wait...'
            : mode === 'signup'
            ? 'Create account'
            : mode === 'forgot'
            ? 'Send reset link'
            : 'Sign in'}
        </button>

        {mode === 'forgot' ? (
          <button style={styles.btnGhost} onClick={() => switchMode('login')}>
            Back to sign in
          </button>
        ) : (
          <button
            style={styles.btnGhost}
            onClick={() => switchMode(mode === 'login' ? 'signup' : 'login')}
          >
            {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
          </button>
        )}
      </div>
    </div>
  )
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f9f9f7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'DM Sans', sans-serif",
    padding: '20px',
  },
  card: {
    background: 'white',
    borderRadius: '24px',
    padding: '40px 32px',
    width: '100%',
    maxWidth: '380px',
    boxShadow: '0 2px 20px rgba(0,0,0,0.06)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  brand: {
    fontFamily: "'DM Serif Display', serif",
    fontSize: '36px',
    color: '#1a1a18',
    textAlign: 'center',
    marginBottom: '4px',
  },
  brandGreen: { color: '#1D9E75' },
  tagline: {
    fontSize: '14px',
    color: '#888',
    textAlign: 'center',
    marginBottom: '12px',
    lineHeight: '1.5',
  },
  nameRow: {
    display: 'flex',
    gap: '8px',
  },
  input: {
    flex: 1,
    padding: '12px 14px',
    borderRadius: '10px',
    border: '0.5px solid #ddd',
    fontSize: '14px',
    fontFamily: "'DM Sans', sans-serif",
    outline: 'none',
    background: '#f9f9f7',
  },
  btnPrimary: {
    padding: '13px',
    background: '#1D9E75',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
    marginTop: '4px',
  },
  btnGhost: {
    padding: '11px',
    background: 'none',
    border: '0.5px solid #ddd',
    borderRadius: '12px',
    fontSize: '13px',
    color: '#888',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
  forgotLink: {
    background: 'none',
    border: 'none',
    fontSize: '12px',
    color: '#1D9E75',
    cursor: 'pointer',
    textAlign: 'right',
    fontFamily: "'DM Sans', sans-serif",
    padding: '0',
    marginTop: '-4px',
  },
  error: {
    background: '#FCEBEB',
    color: '#A32D2D',
    padding: '10px 14px',
    borderRadius: '10px',
    fontSize: '13px',
  },
  messageBox: {
    background: '#E1F5EE',
    color: '#0F6E56',
    padding: '10px 14px',
    borderRadius: '10px',
    fontSize: '13px',
  },
}
